import { useDeleteAvatar, useUploadAvatar } from "@/shared/hooks/useProfile";
import React, { useCallback, useRef, useState } from "react";

import styles from "./AvatarUpload.module.css";

interface AvatarUploadProps {
  currentAvatar?: string;
  username: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const sizeClassMap = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
} as const;

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  username,
  className = "",
  size = "md",
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatarMutation = useUploadAvatar();
  const deleteAvatarMutation = useDeleteAvatar();

  const firstLetter = username.charAt(0).toUpperCase();

  // Валидация файла
  const validateFile = (file: File): string | null => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      return "Поддерживаются только форматы: JPEG, PNG, GIF, WebP";
    }

    if (file.size > maxSize) {
      return "Размер файла не должен превышать 5MB";
    }

    return null;
  };

  // Создание предварительного просмотра
  const createPreview = (file: File): Promise<void> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewImage(result);
        resolve();
      };
      reader.readAsDataURL(file);
    });
  };

  // Обработка выбранного файла
  const handleFileSelect = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        alert(validationError);
        return;
      }

      try {
        setIsUploading(true);
        await createPreview(file);

        await uploadAvatarMutation.mutateAsync({ avatar: file });

        // Очищаем предварительный просмотр после успешной загрузки
        setPreviewImage(null);
      } catch (error) {
        console.error("Ошибка загрузки аватарки:", error);
        setPreviewImage(null);

        // Показываем ошибку пользователю
        if (error instanceof Error) {
          alert(error.message);
        } else {
          alert("Произошла ошибка при загрузке аватарки");
        }
      } finally {
        setIsUploading(false);
      }
    },
    [uploadAvatarMutation]
  );

  // Обработка drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // Обработка клика по аватару
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Обработка удаления аватарки
  const handleDeleteAvatar = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (window.confirm("Вы уверены, что хотите удалить аватарку?")) {
      try {
        await deleteAvatarMutation.mutateAsync();
      } catch (error) {
        console.error("Ошибка удаления аватарки:", error);
        alert("Произошла ошибка при удалении аватарки");
      }
    }
  };

  // Обработка изменения файла через input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Очищаем input для возможности повторной загрузки того же файла
    e.target.value = "";
  };

  // Определяем какой аватар показывать
  const displayImage = previewImage || currentAvatar;
  const showPlaceholder = !displayImage && !isUploading;

  const cx = (...classes: Array<string | undefined | false>) =>
    classes.filter(Boolean).join(" ");

  return (
    <div className={cx(styles.wrapper, className)}>
      <div
        className={cx(
          styles.avatar,
          sizeClassMap[size],
          isDragOver ? styles.dragOver : undefined,
          isUploading ? styles.uploading : undefined
        )}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={cx(styles.baseBg, displayImage ? styles.isHidden : undefined)} />

        {showPlaceholder && <div className={styles.placeholderBg} />}

        {displayImage && (
          <img
            src={displayImage}
            alt={`Аватар ${username}`}
            className={styles.image}
          />
        )}

        {showPlaceholder && (
          <span className={styles.initial}>{firstLetter}</span>
        )}

        {isUploading && (
          <div className={styles.uploadOverlay}>
            <div className={styles.spinner} />
          </div>
        )}

        {isDragOver && !isUploading && (
          <div className={styles.dropOverlay}>
            <span className={styles.dropText}>Отпустите файл</span>
          </div>
        )}

        {currentAvatar && !isUploading && (
          <button
            onClick={handleDeleteAvatar}
            className={styles.deleteButton}
            title="Удалить аватарку"
          >
            ×
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        className={styles.fileInput}
      />

      <div className={styles.tooltip}>
        {currentAvatar ? "Нажмите для смены" : "Нажмите для загрузки"}
      </div>

      {size === "lg" && (
        <>
          <div className={styles.lgHoverRing} />
        </>
      )}
    </div>
  );
};
